import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import * as entra from '../auth/entraAuth';
import * as google from '../auth/googleSignIn';
import * as native from '../auth/nativeAuth';
import type { AuthMode, SignupAttributes } from '../auth/nativeAuth.types';
import { claimsToUser } from '../auth/entraConfig';
import { resolveApiBase } from '../config/env';
import { setUnauthorizedHandler } from '../api/client';

function currentPath(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.pathname + window.location.search;
  }
  return '/';
}

// Profil utilisateur de l'app (forme INCHANGÉE : les écrans consommateurs ne
// changent pas). Désormais alimenté par les claims du token Entra.
export interface AuthUser {
  id: string;
  prenom: string;
  nom: string;
  email: string;
  phone: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  /** Lance le login Entra (PKCE). signup=true -> inscription. returnTo = page à
   *  restaurer après authentification (défaut: page courante). */
  login: (signup?: boolean, returnTo?: string) => Promise<void>;
  /** Connexion via un fournisseur tiers fédéré (Google) en flux navigateur
   *  (Custom Tab natif / redirection web). Web : redirige (ne résout pas).
   *  Natif : ouvre le Custom Tab puis ouvre la session. */
  loginWithGoogle: () => Promise<void>;
  /** Native auth (web) : démarre le flux. mode='signin' -> OTP envoyé ;
   *  mode='signup' -> email inconnu, l'app doit collecter les attributs puis appeler startSignup. */
  startEmailOtp: (email: string) => Promise<{ mode: AuthMode; target?: string }>;
  /** Native auth (web) : inscription avec attributs obligatoires -> envoie l'OTP. */
  startSignup: (email: string, attrs: SignupAttributes) => Promise<{ target?: string }>;
  /** Native auth (web) : valide le code OTP et ouvre la session. À l'inscription,
   *  `profile` est fourni -> le profil est créé/MAJ en base (POST /me/profile). */
  submitOtp: (code: string, profile?: SignupAttributes) => Promise<void>;
  logout: () => void;
  /** Token d'accès courant (pour appeler /me, /me/stories...). */
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRef = useRef<string | null>(null);

  const applySession = (s: { accessToken: string; claims: any } | null) => {
    if (!s) return;
    tokenRef.current = s.accessToken;
    setUser(claimsToUser(s.claims) as AuthUser);
  };

  useEffect(() => {
    (async () => {
      try {
        // 0) Retour de redirection Google (web) : /auth/google/callback.
        const fromGoogle = await google.completeGoogleIfCallback();
        if (fromGoogle) {
          applySession(fromGoogle);
          setIsLoading(false);
          return;
        }
        // 1) Retour de redirection Entra (web).
        const fromCallback = await entra.completeLoginIfCallback();
        if (fromCallback) {
          applySession(fromCallback);
          // returnTo : restaurer la page visée avant l'auth.
          const rt = entra.consumeReturnTo();
          if (rt && Platform.OS === 'web' && typeof window !== 'undefined' && rt !== currentPath()) {
            window.location.replace(rt);
            return;
          }
        } else {
          // 2) sinon session stockée.
          applySession(await entra.restoreSession());
        }
      } catch (e) {
        // Auth indisponible / annulée -> on reste déconnecté.
        // eslint-disable-next-line no-console
        console.warn('Auth bootstrap:', e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Intercepteur 401 global : token expiré/revoqué -> vider la session +
  // mémoriser la page courante ; la bascule user=null fait afficher le
  // PublicNavigator qui redirige vers l'écran de connexion (returnTo).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      entra.rememberReturnTo(currentPath());
      tokenRef.current = null;
      setUser(null);
    });
  }, []);

  const login = async (signup = false, returnTo?: string): Promise<void> => {
    // returnTo passé seulement s'il est explicite : sinon on conserve celui
    // déjà mémorisé par le guard / l'intercepteur 401 (ne pas l'écraser).
    // Web : redirige vers Entra (ne résout pas). Natif : résout avec la session.
    const s = (await entra.startLogin(signup, returnTo)) as any;
    applySession(s);
  };

  const loginWithGoogle = async (): Promise<void> => {
    // Google Sign-In DIRECT (hors Entra) : id_token Google -> session backend.
    // Web (GIS) + natif (lib google-signin) renvoient une Session -> applySession.
    const s = await google.loginWithGoogle();
    applySession(s);
  };

  const startEmailOtp = (email: string) => native.startEmailOtp(email);

  const startSignup = (email: string, attrs: SignupAttributes) => native.startSignup(email, attrs);

  const submitOtp = async (code: string, profile?: SignupAttributes): Promise<void> => {
    const s = await native.submitOtp(code);
    // Inscription : créer/MAJ le profil EN BASE (champs dupliqués depuis Entra +
    // age, champ custom DB-only). Le compte existe déjà côté Entra.
    if (profile) {
      try {
        await fetch(`${resolveApiBase()}/me/profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.accessToken}` },
          body: JSON.stringify({
            firstName: profile.givenName,
            lastName: profile.surname,
            country: profile.country,
            age: parseInt(profile.age, 10) || null,
          }),
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Profil non enregistré en base:', e);
      }
    }
    applySession(s);
  };

  const logout = () => {
    tokenRef.current = null;
    setUser(null);
    // Native auth : purge LOCALE (pas de redirection vers ciamlogin/logout).
    native.logout();
  };

  const getAccessToken = () => tokenRef.current;

  return (
    <AuthContext.Provider value={{ user, isLoading, login, loginWithGoogle, startEmailOtp, startSignup, submitOtp, logout, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
};
